import { useCallback, useMemo, useState } from 'react';
import { stopCameraVideoStream } from 'services/devices/cameras/camera.service';
import { PLAYBACK_STATUS } from '../config/videoPlayer.config';
import { getCameraIdFromVideoStreamUrl } from '../utils/videoPlayer.utils';

/**
 * Owns the high-level lifecycle for the current video source.
 *
 * The hook resets the player when a source is stopped or replaced, clears loaded-video
 * progress, pauses or resets the watch-time timer, can force React to create a fresh
 * video element, and tells the backend when this viewer is done watching the stream.
 */
const useVideoSourceLifecycle = ({
  clearStream,
  pauseWatchTimeLimitTimer,
  resetReadyVideoProgress,
  resetWatchTimeLimitTimer,
  src,
  startNewPlaybackRequest,
  videoRef,
}) => {
  const [mediaElementKey, setMediaElementKey] = useState(0);

  const resetVideoPlayer = useCallback(
    ({
      nextStatus = PLAYBACK_STATUS.idle,
      remountMedia = false,
      resetWatchTime = false,
      requestId,
    } = {}) => {
      const activeRequestId = requestId ?? startNewPlaybackRequest(nextStatus);
      const video = videoRef.current;

      if (resetWatchTime) resetWatchTimeLimitTimer();
      else pauseWatchTimeLimitTimer();

      clearStream(video); // Tear down HLS and reset the video element to a clean state
      resetReadyVideoProgress(); // Clears the loaded-video progress for the next stream

      if (remountMedia) {
        setMediaElementKey((currentKey) => currentKey + 1); // Forces React to create a new video element
      }

      return activeRequestId;
    },
    [
      clearStream,
      pauseWatchTimeLimitTimer,
      resetReadyVideoProgress,
      resetWatchTimeLimitTimer,
      startNewPlaybackRequest,
      videoRef,
    ],
  );

  const releaseStream = useCallback(() => {
    const cameraId = getCameraIdFromVideoStreamUrl(src);
    if (cameraId) {
      void stopCameraVideoStream(cameraId);
    }
  }, [src]);

  return useMemo(
    () => ({
      mediaElementKey,
      releaseStream,
      resetVideoPlayer,
    }),
    [mediaElementKey, releaseStream, resetVideoPlayer],
  );
};

export default useVideoSourceLifecycle;
